import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ApiConnectionString } from "../../../constants/ApiConntection";
import "./ppf.css";

export default function ProfilePictureUpload({ user, accepted, onToggle, error, onImageStatusChange, imageError }) {
  const inputRef = useRef(null);

  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const setImageAndNotify = (nextUrl) => {
    setImageUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return nextUrl;
    });
    if (onImageStatusChange) onImageStatusChange(!!nextUrl);
  };

  const fetchImageUrlIfExists = async () => {
    if (!user) return;

    try {
      const response = await axios.get(`${ApiConnectionString}/api/image/profile/${user.id}`, {
        headers: {
          Authorization: `Bearer ${user.token}`
        },
        responseType: "blob",
        validateStatus: (status) => (status >= 200 && status < 300) || status === 204
      });

      if (response.status === 204) {
        setImageAndNotify("");
        return;
      }

      const objectUrl = URL.createObjectURL(response.data);
      setImageAndNotify(objectUrl);
    } catch {
      setImageAndNotify("");
    }
  };

  useEffect(() => {
    fetchImageUrlIfExists();

    return () => {
      setImageUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return "";
      });
    };
  }, [user.id]);

  const upload = async (file) => {
    if (!file) return;

    setLoading(true);
    setMessage("");

    try {
      const formData = new FormData();
      formData.append("image", file);

      await axios.post(`${ApiConnectionString}/api/image/profile/`, formData, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      setMessage("Uploaded");
      await fetchImageUrlIfExists();
    } catch (err) {
      setMessage(err?.response?.data || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const remove = async () => {
    setLoading(true);
    setMessage("");

    try {
      await axios.delete(`${ApiConnectionString}/api/image/profile/`, {
        headers: {
          Authorization: `Bearer ${user.token}`
        }
      });

      setMessage("Deleted");

      if (inputRef.current) inputRef.current.value = "";
      await fetchImageUrlIfExists();
    } catch (err) {
      setMessage(err?.response?.data || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pp-upload-card">
      <div className="pp-upload-header">
        <div className="pp-upload-subtitle">Upload a clear headshot for your profile.</div>
      </div>

      {message ? <div className="alert alert-info pp-upload-alert">{message}</div> : null}

      <div className="pp-upload-body">
        <div className="pp-upload-avatar-wrap">
          {imageUrl ? (
            <img src={imageUrl} alt="Profile" className="pp-upload-avatar" />
          ) : (
            <div className="pp-upload-avatar pp-upload-avatar-empty">
              <span className="pp-upload-avatar-empty-text">No image</span>
            </div>
          )}
        </div>

        <div className="pp-upload-actions">
          <input
            ref={inputRef}
            className="form-control bcs-brand-input pp-upload-file"
            type="file"
            accept="image/*"
            disabled={loading}
            onChange={async (e) => {
              const file = e.target.files?.[0] || null;
              if (!file) return;

              await upload(file);

              if (inputRef.current) inputRef.current.value = "";
            }}
          />

          <div className="pp-upload-btn-row">
            <button
              className="btn brand-purple-inline pp-upload-btn"
              type="button"
              disabled={loading}
              onClick={() => inputRef.current && inputRef.current.click()}
            >
              {loading ? "Working" : imageUrl ? "Change image" : "Choose image"}
            </button>

            {imageUrl ? (
              <button className="btn brand-purple-outline pp-upload-btn" onClick={remove} disabled={loading} type="button">
                Remove
              </button>
            ) : null}
          </div>

          <div className="pp-upload-hint">JPG or PNG works best. Square images look great.</div>

          {imageError ? <div className="invalid-feedback d-block mt-2">{imageError}</div> : null}

          <div className="mt-3">
            <div className={`form-check${error ? " is-invalid" : ""}`}>
              <input
                className={`form-check-input ppfCheck${error ? " is-invalid" : ""}`}
                type="checkbox"
                id="acceptedProfileImageTerms"
                name="acceptedProfileImageTerms"
                checked={!!accepted}
                onChange={onToggle}
              />
              <label className="form-check-label ppfLabel" htmlFor="acceptedProfileImageTerms">
                I confirm I have the right to use this image and it is an appropriate profile photo
              </label>
            </div>

            {error ? <div className="invalid-feedback d-block">{error}</div> : null}
          </div>
        </div>
      </div>

      {imageUrl ? (
        <div className="pp-upload-preview">
          <img src={imageUrl} alt="Profile preview" className="pp-upload-preview-img" />
        </div>
      ) : null}
    </div>
  );
}
